// src/api/devIntelApi.js
// RTK Query slice for the Dev Intelligence module.
// Mirrors the existing src/api/apiSlice.js pattern (uses the same baseUrl pattern from src/constant.js).

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { constant } from 'constant';

const baseQuery = fetchBaseQuery({
	baseUrl: `${constant.baseUrl}api/dev-intel`,
	prepareHeaders: (headers) => {
		const token =
			localStorage.getItem('accessToken') ||
			sessionStorage.getItem('accessToken');
		if (token) headers.set('Authorization', `Bearer ${token}`);
		return headers;
	},
});

export const devIntelApi = createApi({
	reducerPath: 'devIntelApi',
	baseQuery,
	tagTypes: ['Update', 'Project', 'Match', 'SourceChannel'],
	endpoints: (builder) => ({
		// ─── Ingestion ─────────────────────────────────────────────────────
		ingestManual: builder.mutation({
			query: (body) => ({ url: '/ingest/manual', method: 'POST', body }),
			invalidatesTags: ['Update', 'Project'],
		}),
		listUpdates: builder.query({
			query: (params = {}) => ({ url: '/updates', params }),
			providesTags: ['Update'],
		}),
		getUpdate: builder.query({
			query: (id) => ({ url: `/updates/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'Update', id }],
		}),
		reprocessUpdate: builder.mutation({
			query: (id) => ({ url: `/updates/${id}/reprocess`, method: 'POST' }),
			invalidatesTags: ['Update', 'Project'],
		}),
		approveUpdate: builder.mutation({
			query: (id) => ({ url: `/updates/${id}/approve`, method: 'POST' }),
			invalidatesTags: ['Update', 'Project'],
		}),

		// ─── Projects ──────────────────────────────────────────────────────
		listProjects: builder.query({
			query: (params = {}) => ({ url: '/projects', params }),
			providesTags: ['Project'],
		}),
		getProject: builder.query({
			query: (id) => ({ url: `/projects/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'Project', id }],
		}),
		updateProject: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `/projects/${id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [{ type: 'Project', id }, 'Project'],
		}),
		deleteProject: builder.mutation({
			query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Project'],
		}),
		addOffer: builder.mutation({
			query: ({ projectId, ...body }) => ({
				url: `/projects/${projectId}/offers`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: 'Project', id: projectId },
			],
		}),

		// ─── Matching ──────────────────────────────────────────────────────
		runMatch: builder.mutation({
			query: (leadId) => ({ url: `/match/lead/${leadId}/run`, method: 'POST' }),
			invalidatesTags: (_r, _e, leadId) => [{ type: 'Match', id: leadId }],
		}),
		getMatch: builder.query({
			query: (leadId) => ({ url: `/match/lead/${leadId}` }),
			providesTags: (_r, _e, leadId) => [{ type: 'Match', id: leadId }],
		}),

		// ─── Ask ───────────────────────────────────────────────────────────
		ask: builder.mutation({
			query: (body) => ({ url: '/ask', method: 'POST', body }),
		}),

		// ─── Source channels ───────────────────────────────────────────────
		listSourceChannels: builder.query({
			query: () => ({ url: '/source-channels' }),
			providesTags: ['SourceChannel'],
		}),
		createSourceChannel: builder.mutation({
			query: (body) => ({ url: '/source-channels', method: 'POST', body }),
			invalidatesTags: ['SourceChannel'],
		}),
		updateSourceChannel: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `/source-channels/${id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['SourceChannel'],
		}),
		deleteSourceChannel: builder.mutation({
			query: (id) => ({ url: `/source-channels/${id}`, method: 'DELETE' }),
			invalidatesTags: ['SourceChannel'],
		}),
	}),
});

export const {
	useIngestManualMutation,
	useListUpdatesQuery,
	useGetUpdateQuery,
	useReprocessUpdateMutation,
	useApproveUpdateMutation,
	useListProjectsQuery,
	useGetProjectQuery,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	useAddOfferMutation,
	useRunMatchMutation,
	useGetMatchQuery,
	useAskMutation,
	useListSourceChannelsQuery,
	useCreateSourceChannelMutation,
	useUpdateSourceChannelMutation,
	useDeleteSourceChannelMutation,
} = devIntelApi;
